import { useEffect, useMemo, useRef, useState } from "react";
import {
  hasTelegramLaunchContext,
  loadTelegramSdk,
  telegramHaptic,
  telegramWebApp,
} from "../../platform/telegram.js";
import { useAuth } from "./AuthProvider.jsx";
import { exchangeTelegramMiniApp, fetchCurrentUser } from "./api.js";

const exchangeByWebApp = new WeakMap();
const PUBLIC_PATHS = ["/p/", "/auth/"];

function safeNextPath(value) {
  return value === "/onboarding" ? "/onboarding" : "/";
}

function exchangeOnce(webApp, auth) {
  const current = exchangeByWebApp.get(webApp);
  if (current) return current;
  const pending = (async () => {
    const exchange = await exchangeTelegramMiniApp(webApp.initData);
    const credential = await auth.signInWithCustomToken(exchange.customToken);
    const idToken = await credential?.user?.getIdToken?.()
      || await auth.getIdToken(true);
    if (!idToken) throw new Error("missing-firebase-session");
    await fetchCurrentUser(idToken);
    return exchange;
  })();
  exchangeByWebApp.set(webApp, pending);
  pending.catch(() => exchangeByWebApp.delete(webApp));
  return pending;
}

export function TelegramAuthBootstrap({ children }) {
  const auth = useAuth();
  const telegramLaunch = useMemo(() => hasTelegramLaunchContext(), []);
  const [webApp, setWebApp] = useState(() => telegramWebApp());
  const bypass = PUBLIC_PATHS.some((prefix) => globalThis.location?.pathname?.startsWith(prefix));
  const [phase, setPhase] = useState(telegramLaunch && !bypass ? "waiting" : "ready");
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (webApp || !telegramLaunch || bypass) return;
    void loadTelegramSdk().then((loaded) => {
      if (!mounted.current) return;
      if (loaded) {
        setWebApp(loaded);
        return;
      }
      setError("Telegram не ответил. Проверьте соединение и откройте AxonLeads из бота ещё раз.");
      setPhase("error");
    });
  }, [bypass, telegramLaunch, webApp]);

  useEffect(() => {
    if (!webApp || bypass) return;
    try {
      webApp.setHeaderColor?.("#F3F1E8");
      webApp.setBackgroundColor?.("#F3F1E8");
      webApp.expand?.();
      webApp.ready?.();
    } catch {
      // Авторизация не зависит от декоративных методов Telegram.
    }
  }, [bypass, webApp]);

  useEffect(() => {
    if (!webApp || bypass || auth.status === "loading") return;
    // После ошибки ждём явного нажатия «Повторить вход». Без этого guard смена
    // фазы сразу запускала новый обмен и создавала невидимый цикл запросов.
    if (phase === "exchanging" || phase === "error") return;
    if (auth.status === "authenticated") {
      setPhase("ready");
      return;
    }
    if (auth.status === "error") {
      setError("Не получилось восстановить защищённую сессию.");
      setPhase("error");
      return;
    }
    setPhase("exchanging");
    setError("");
    void exchangeOnce(webApp, auth)
      .then((result) => {
        if (!mounted.current) return;
        const nextPath = safeNextPath(result.nextPath);
        if (globalThis.location?.pathname === "/" && nextPath !== "/") {
          globalThis.history?.replaceState?.({}, "", nextPath);
        }
        telegramHaptic("success");
        setPhase("ready");
      })
      .catch(() => {
        if (!mounted.current) return;
        telegramHaptic("error");
        setError("Не получилось подтвердить вход. Откройте AxonLeads из бота ещё раз или повторите попытку.");
        setPhase("error");
      });
  }, [attempt, auth, bypass, phase, webApp]);

  if (phase === "ready") return children;

  return (
    <main className="telegram-auth-gate" aria-busy={phase !== "error"}>
      <section className="telegram-auth-gate__card" aria-live="polite">
        <span className="type-meta">AXONLEADS / TELEGRAM</span>
        <h1>{phase === "error" ? "Вход не завершён" : "Открываем ваш кабинет"}</h1>
        <p>
          {phase === "error"
            ? error
            : "Подтверждаем вход через Telegram и загружаем ваши заказы, клиентов и проекты."}
        </p>
        {phase === "error" ? (
          <button
            type="button"
            onClick={() => {
              setPhase("waiting");
              setAttempt((value) => value + 1);
            }}
          >
            Повторить вход
          </button>
        ) : <span className="telegram-auth-gate__signal" aria-hidden="true" />}
      </section>
    </main>
  );
}
